import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { FileCode2, GitBranch, ScrollText, Database, Plug, RefreshCw } from "lucide-react";

const TABS = [
  { id: "pages", label: "Pages & code", icon: FileCode2 },
  { id: "git", label: "Versionning", icon: GitBranch },
  { id: "regles", label: "Règles de développement", icon: ScrollText },
  { id: "bdd", label: "Base de données", icon: Database },
  { id: "integrations", label: "Intégrations", icon: Plug },
];

const REGLES = [
  { titre: "Authentification", regle: "Sans mot de passe : magic link valable 15 min (fallback code 6 chiffres lié à l'email demandeur, 10 tentatives/15 min par email+IP), session cookie HttpOnly cfhtb_session (30 j). Ne jamais ajouter de champ mot de passe. Un membre peut se connecter avec son email principal ou l'une de ses adresses secondaires (emails_secondaires, unicité inter-comptes vérifiée). Connexion de secours admin : 5 clics sur le logo de /connexion (secrets en .env, anti force brute par IP)." },
  { titre: "Emails transactionnels", regle: "Envoi via brevo_send() : relais SMTP Brevo avec logo CFHTB embarqué inline (CID), fallback API REST. Expéditeur et signatures gérés en base (Messagerie → Paramètres), jamais dans le code. Signatures multiples (collection signatures) sélectionnables sur campagnes et onboarding ; test d'envoi obligatoire avant toute campagne." },
  { titre: "HelloAsso", regle: "Mapping par formulaire (collection helloasso_mappings) : webinaire → événement, adhésion → période fixe ou glissante. Synchro idempotente (helloasso_synced) déclenchée manuellement ou par webhook /api/helloasso/webhook. Rapprochement par email principal OU secondaire. Doublon (même nom+prénom, autre email) → remarque admin, fusion manuelle uniquement (Membres → Doublons), annulable 30 j." },
  { titre: "Routes backend", regle: "Toutes les routes FastAPI sont préfixées /api (routage ingress Kubernetes). Le frontend appelle uniquement via process.env.REACT_APP_BACKEND_URL." },
  { titre: "Fichiers & liens privés", regle: "Jamais d'URL externe exposée au client : documents servis par URLs signées HMAC (5 min), vidéos pCloud via proxy signé /api/medias/replay/{token} (15 min)." },
  { titre: "MongoDB", regle: "ObjectId converti en str via clean() — jamais de document Mongo brut retourné. Datetimes toujours timezone-aware (datetime.now(timezone.utc))." },
  { titre: "Langue", regle: "Tous les textes UI, emails, notifications et messages d'erreur sont en français." },
  { titre: "Audit", regle: "Toute action sensible (création/modification/suppression, imports/exports, envois, lectures de replay, synchros HelloAsso, fusions) écrit dans audit_logs." },
  { titre: "Charte graphique", regle: "Couleurs cfhtb-* (primaire #282194), polices Domine (titres) et Poppins (texte), composants shadcn/ui, layout mobile-first." },
  { titre: "Tests", regle: "data-testid obligatoire sur tout élément interactif et toute info utilisateur critique. Suite pytest autonome : fixtures créées puis nettoyées par conftest à chaque run ; la configuration admin réelle (mappings HelloAsso, signatures) est sauvegardée avant le run et restaurée après." },
  { titre: "Secrets & configuration", regle: "Uniquement via fichiers .env (MONGO_URL, DB_NAME, BREVO_API_KEY, BREVO_SMTP_*…). Aucune clé en dur dans le code." },
  { titre: "Hot reload", regle: "Frontend et backend rechargent automatiquement. Redémarrage supervisor uniquement après modification de .env ou installation de dépendances." },
];

export default function ArchitectureAdmin() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("pages");
  const [integ, setInteg] = useState(null);
  const [integLoading, setIntegLoading] = useState(false);
  const [integError, setIntegError] = useState(null);

  useEffect(() => {
    api("/admin/architecture").then(setData).catch((e) => setError(e.message));
  }, []);

  const chargerIntegrations = () => {
    setIntegLoading(true);
    api("/admin/architecture/integrations")
      .then((res) => { setInteg(res); setIntegError(null); })
      .catch((e) => setIntegError(e.message))
      .finally(() => setIntegLoading(false));
  };

  useEffect(() => {
    if (tab === "integrations" && !integ) chargerIntegrations();
  }, [tab]);

  const grandTotal = data?.groupes.reduce((s, g) => s + g.total_lignes, 0) || 0;

  return (
    <AppLayout variant="admin">
      <div data-testid="architecture-page" className="space-y-6 animate-fade-in">
        <div>
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Architecture</h1>
          <p className="text-sm text-cfhtb-text mt-1">
            État technique du projet, généré en temps réel depuis le code, git et MongoDB.
          </p>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="architecture-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`arch-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id
                  ? "border-cfhtb-primary text-cfhtb-primary"
                  : "border-transparent text-cfhtb-text hover:text-cfhtb-heading"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error" data-testid="architecture-error">{error}</div>
        ) : !data ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            {tab === "pages" && (
              <div className="space-y-5" data-testid="arch-pages-panel">
                <p className="text-sm text-cfhtb-text">
                  Total du projet : <span className="font-semibold text-cfhtb-heading" data-testid="arch-total-lignes">{grandTotal.toLocaleString("fr-FR")} lignes</span>
                </p>
                {data.groupes.map((g) => (
                  <div key={g.groupe} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
                      <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">{g.groupe}</h2>
                      <span className="text-xs font-medium text-cfhtb-secondary">{g.fichiers.length} fichier(s) · {g.total_lignes.toLocaleString("fr-FR")} lignes</span>
                    </div>
                    <ul className="divide-y divide-slate-50">
                      {g.fichiers.map((f) => (
                        <li key={f.chemin} className="flex items-center justify-between px-5 py-2 text-sm">
                          <span className="text-cfhtb-heading font-mono text-xs sm:text-sm">{f.nom}</span>
                          <span className="text-cfhtb-text tabular-nums">{f.lignes.toLocaleString("fr-FR")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {tab === "git" && (
              <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden" data-testid="arch-git-panel">
                <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
                  <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Historique des versions (git)</h2>
                </div>
                {data.commits.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-cfhtb-text">Aucun commit trouvé.</p>
                ) : (
                  <ul className="divide-y divide-slate-50">
                    {data.commits.map((c, i) => (
                      <li key={`${c.hash}-${i}`} className="px-5 py-3 flex items-start gap-4">
                        <code className="text-xs bg-cfhtb-bg text-cfhtb-primary px-2 py-1 rounded font-mono shrink-0">{c.hash}</code>
                        <div className="min-w-0">
                          <p className="text-sm text-cfhtb-heading leading-snug">{c.message}</p>
                          <p className="text-xs text-cfhtb-text mt-0.5">{c.date}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === "regles" && (
              <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid="arch-regles-panel">
                <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
                  <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Règles de développement du projet</h2>
                </div>
                <ul className="divide-y divide-slate-50">
                  {REGLES.map((r) => (
                    <li key={r.titre} className="px-5 py-3.5">
                      <p className="text-sm font-semibold text-cfhtb-heading">{r.titre}</p>
                      <p className="text-sm text-cfhtb-text mt-0.5 leading-relaxed">{r.regle}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tab === "integrations" && (
              <div className="space-y-4" data-testid="arch-integrations-panel">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-cfhtb-text">
                    {integ?.verifie_le
                      ? <>Vérifié en temps réel le <span data-testid="integrations-date" className="font-medium text-cfhtb-heading">{new Date(integ.verifie_le).toLocaleString("fr-FR")}</span></>
                      : "Vérification en temps réel des services externes."}
                  </p>
                  <button
                    data-testid="integrations-refresh"
                    onClick={chargerIntegrations}
                    disabled={integLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md border border-cfhtb-light/40 text-cfhtb-primary hover:bg-cfhtb-bg transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${integLoading ? "animate-spin" : ""}`} /> Actualiser
                  </button>
                </div>
                {integError ? (
                  <div className="bg-white rounded-lg border border-cfhtb-error/30 p-6 text-sm text-cfhtb-error" data-testid="integrations-error">{integError}</div>
                ) : integLoading && !integ ? (
                  <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <div className="grid gap-4 sm:grid-cols-3">
                    {(integ?.items || []).map((it) => (
                      <div key={it.id} data-testid={`integration-card-${it.id}`}
                           className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold text-cfhtb-heading leading-snug">{it.nom}</h3>
                          {!it.configure ? (
                            <span data-testid={`integration-statut-${it.id}`} className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Non configuré</span>
                          ) : it.connecte ? (
                            <span data-testid={`integration-statut-${it.id}`} className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Connecté</span>
                          ) : (
                            <span data-testid={`integration-statut-${it.id}`} className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">Erreur</span>
                          )}
                        </div>
                        <ul className="text-xs text-cfhtb-text space-y-1">
                          {it.mode && <li>Mode : {it.mode}</li>}
                          {it.detail && <li className="break-all">{it.detail}</li>}
                          {it.extra && <li>{it.extra}</li>}
                          {it.latence_ms != null && <li className="tabular-nums">Latence : {it.latence_ms} ms</li>}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "bdd" && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="arch-bdd-panel">
                {data.collections.map((c) => (
                  <div key={c.nom} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5" data-testid={`arch-collection-${c.nom}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="font-mono text-sm font-semibold text-cfhtb-primary">{c.nom}</h3>
                      <span className="text-xs text-cfhtb-text">{c.nb_documents.toLocaleString("fr-FR")} document(s)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {c.champs.map((ch) => (
                        <span key={ch} className="text-[11px] font-mono bg-cfhtb-bg text-cfhtb-text px-1.5 py-0.5 rounded">{ch}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
